import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import Header from "../components/Header";
import useAdminOnly from "../hooks/useAdminOnly";

const LOGO_URL = "https://cdn.outfall.website/Images/Logo/OutFall%20Logo%20Orange.png";

export default function CareersDepth() {
  const { id } = useParams();
  const { allowed } = useAdminOnly();

  const [application, setApplication] = useState(null);
  const [status, setStatus] = useState("submitted");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    loadApplication();
  }, [id]);

  async function loadApplication() {
    setLoading(true);

    const { data, error } = await supabase
      .from("career_applications")
      .select(
        `
        id,
        status,
        archived,
        applicant_data,
        created_at,
        discord_id,
        discord_username,
        careers (
          id,
          title,
          department,
          location,
          type
        )
      `,
      )
      .eq("id", id)
      .single();

    if (!error && data) {
      setApplication(data);
      setStatus(data.status);
    }

    setLoading(false);
  }

  async function updateStatus() {
    setSaving(true);

    try {
      const { error } = await supabase
        .from("career_applications")
        .update({ status })
        .eq("id", id);

      if (error) throw error;

      const applicantData = application.applicant_data || {};
      const applicantEmail =
        applicantData.email || applicantData.Email || applicantData.email_address ||
        applicantData.Email_Address || applicantData.contact_email;
      const applicantName =
        applicantData.full_name || applicantData.Full_Name ||
        applicantData.name || applicantData.Name || "Applicant";

      // Send email if we have one
      if (applicantEmail) {
        await fetch("/api/send-career-status", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: applicantEmail,
            applicantName,
            status,
            roleTitle: application.careers?.title || "Outfall Careers",
            logoUrl: LOGO_URL,
            bannerUrl: "",
          }),
        });
      }

      // Send Discord DM if we have a discord_id
      if (application.discord_id) {
        await fetch("/api/send-discord-dm", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            discord_id: application.discord_id,
            discord_username: application.discord_username,
            status,
            role_title: application.careers?.title,
            secret: process.env.REACT_APP_DM_WEBHOOK_SECRET,
          }),
        });
      }

      setApplication((prev) => ({ ...prev, status }));
      alert("Status saved" + (application.discord_id ? " and Discord DM sent." : "."));
    } catch (err) {
      console.error(err);
      alert("Failed to save status. Check console.");
    }

    setSaving(false);
  }

  async function toggleArchive() {
    setArchiving(true);
    const newVal = !application.archived;
    const { error } = await supabase
      .from("career_applications")
      .update({ archived: newVal })
      .eq("id", id);

    if (!error) setApplication(prev => ({ ...prev, archived: newVal }));
    setArchiving(false);
  }

  function renderValue(value) {
    if (!value) return "N/A";

    if (typeof value === "object" && value.url) {
      return (
        <a
          href={value.url}
          target="_blank"
          rel="noopener noreferrer"
          className="font-black text-[#ffd978] underline"
        >
          {value.name || "View File"}
        </a>
      );
    }

    if (typeof value === "object") {
      return (
        <pre className="overflow-x-auto rounded-xl bg-black/60 p-4 text-sm text-white">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    return String(value);
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-[#050505] text-white">
        <Header />
        <section className="px-6 pt-36 md:px-[7%]">
          <p className="text-xl font-black">Loading application...</p>
        </section>
      </main>
    );
  }

  if (!application) {
    return (
      <main className="min-h-screen bg-[#050505] text-white">
        <Header />
        <section className="px-6 pt-36 md:px-[7%]">
          <h1 className="outfall-heading text-7xl text-[#ffd978]">Application Not Found</h1>
          <Link to="/careers-review" className="outfall-button mt-6">Back To Applications</Link>
        </section>
      </main>
    );
  }

  const applicantData = application.applicant_data || {};

  if (allowed === null) return null;
  if (!allowed) return (
    <main className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
      <Header />
      <p className="text-white/40 text-sm mt-32">You don't have permission to view this page.</p>
    </main>
  );

  return (
    <main className="min-h-screen bg-[#050505] text-white">
      <Header />

      <section className="px-6 pt-36 pb-24 md:px-[7%]">
        <div className="flex items-center justify-between gap-4">
          <Link
            to="/careers-review"
            className="font-black uppercase text-[#ffb56f] hover:text-[#ffd978]"
          >
            ← Back To Applications
          </Link>
          <button
            onClick={toggleArchive}
            disabled={archiving}
            className={`rounded-xl border px-4 py-2 text-sm font-black uppercase transition disabled:opacity-40 ${
              application.archived
                ? "border-[#ffd978]/40 text-[#ffd978] hover:bg-[#ffd978]/10"
                : "border-white/20 text-white/40 hover:border-white/40 hover:text-white/70"
            }`}
          >
            {archiving ? "..." : application.archived ? "Unarchive" : "Archive"}
          </button>
        </div>

        {application.archived && (
          <div className="mt-4 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-bold text-white/40">
            This application is archived — it won't appear in the main list.
          </div>
        )}

        <p className="mt-8 font-black uppercase text-[#ffb56f]">Application Review</p>

        <h1 className="outfall-heading text-[clamp(58px,9vw,120px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
          {application.careers?.title || "Unknown Role"}
        </h1>

        <div className="mt-6 grid gap-4 md:grid-cols-4">
          <div className="rounded-2xl border border-[#ffb56f]/30 bg-black/50 p-5">
            <p className="font-black uppercase text-[#ffb56f]">Department</p>
            <p className="mt-2 font-black text-white">{application.careers?.department || "N/A"}</p>
          </div>
          <div className="rounded-2xl border border-[#ffb56f]/30 bg-black/50 p-5">
            <p className="font-black uppercase text-[#ffb56f]">Location</p>
            <p className="mt-2 font-black text-white">{application.careers?.location || "N/A"}</p>
          </div>
          <div className="rounded-2xl border border-[#ffb56f]/30 bg-black/50 p-5">
            <p className="font-black uppercase text-[#ffb56f]">Type</p>
            <p className="mt-2 font-black text-white">{application.careers?.type || "N/A"}</p>
          </div>
          <div className="rounded-2xl border border-[#ffb56f]/30 bg-black/50 p-5">
            <p className="font-black uppercase text-[#ffb56f]">Submitted</p>
            <p className="mt-2 font-black text-white">{new Date(application.created_at).toLocaleString()}</p>
          </div>
        </div>

        {application.discord_id && (
          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-[#5865F2]/30 bg-[#5865F2]/10 p-4">
            <svg width="20" height="15" viewBox="0 0 24 18" fill="#5865F2">
              <path d="M20.317 1.492a19.825 19.825 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.294 18.294 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 1.492a.07.07 0 0 0-.032.027C.533 6.093-.32 10.555.099 14.961a.08.08 0 0 0 .031.055 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.442a.061.061 0 0 0-.031-.028z"/>
            </svg>
            <span className="font-black text-[#5865F2]">
              {application.discord_username || application.discord_id}
            </span>
            <span className="text-sm font-bold text-white/40">· Status changes will DM this user</span>
          </div>
        )}

        <div className="mt-10 rounded-[28px] border-4 border-[#ffd978] bg-black/60 p-6 shadow-[0_0_35px_rgba(255,143,107,0.35)] md:p-10">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h2 className="outfall-heading text-6xl text-[#ffd978]">Applicant Data</h2>

            <div className="flex flex-wrap gap-3">
              <select
                className="rounded-xl border border-[#ffb56f]/30 bg-black/70 px-4 py-3 font-black uppercase text-white outline-none"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="submitted">Submitted</option>
                <option value="reviewing">Reviewing</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>

              <button
                onClick={updateStatus}
                disabled={saving}
                className="outfall-button disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Status"}
              </button>
            </div>
          </div>

          <div className="mt-8 grid gap-5">
            {Object.entries(applicantData).map(([key, value]) => (
              <div key={key} className="rounded-2xl border border-[#ffb56f]/30 bg-black/40 p-5">
                <p className="mb-2 font-black uppercase text-[#ffb56f]">
                  {key.replaceAll("_", " ")}
                </p>
                <div className="text-lg font-bold text-white select-text cursor-text">{renderValue(value)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
